import React from "react";
import "./_stepper.scss";

type StepState = "active" | "complete" | "incomplete" | "error";

interface StepperContextValue {
  activeStep: number;
  state?: StepState;
  onClickStep?: (step: number) => void;
  clickable: boolean;
  stepCount: number;
}

interface StepperProps {
  className?: string;
  activeStep: number;
  children: React.ReactNode;
  state?: StepState;
  onClickStep?: (step: number) => void;
}

interface StepProps {
  index?: number;
  isCompletedStep?: boolean;
  isCurrentStep?: boolean;
  isLastStep?: boolean;
  children?: React.ReactNode;
}

const StepperContext = React.createContext<StepperContextValue | undefined>(undefined);

export const useStepperContext = () => {
  const context = React.useContext(StepperContext);
  if (!context) {
    throw new Error("useStepperContext must be used within a StepperProvider");
  }
  return context;
};

export const Stepper: React.FC<StepperProps> = props => {
  const { className = "", activeStep, children, state, onClickStep, ...rest } = props;

  const childArr = React.Children.toArray(children);
  const stepCount = childArr.length;

  const renderHorizontalContent = () => {
    if (activeStep <= childArr.length) {
      return React.Children.map(childArr[activeStep], node => {
        if (!React.isValidElement(node)) return null;
        return React.Children.map(node.props.children, childNode => childNode);
      });
    }
    return null;
  };

  const clickable = !!onClickStep;

  const contextValue: StepperContextValue = {
    activeStep,
    state,
    onClickStep,
    clickable,
    stepCount
  };

  return (
    <StepperContext.Provider value={contextValue}>
      <div
        className={`row m-0 steps-container ${className} d-flex flex-row align-self-center ${
          stepCount === 1 ? "justify-content-end" : "justify-content-between"
        }`}
        {...rest}
      >
        {React.Children.map(children, (child, i) => {
          if (!React.isValidElement(child)) return null;

          const isCompletedStep = child.props.isCompletedStep ?? i < activeStep;
          const isLastStep = i === stepCount - 1;
          const isCurrentStep = i === activeStep;

          const stepProps: StepProps = {
            index: i,
            isCompletedStep,
            isCurrentStep,
            isLastStep
          };

          return React.cloneElement(child, stepProps);
        })}
      </div>
      {renderHorizontalContent()}
    </StepperContext.Provider>
  );
};

export default Stepper;
